import React, { useEffect, useMemo, useState } from 'react';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import LgmButton from './LgmButton';
import { CasSourceCard, computeFieldConflicts } from './CasSourceCard';
import { useApi } from '../services/api/provider';
import { ProductService } from '../services/api/domains/products/service';
import type {
  CasQuarantineCandidate,
  CasQuarantineRawCandidate,
  CasQuarantineReviewRow,
  CasQuarantineSourceRow,
} from '../services/api/domains/products/types';

/**
 * Decision panel for one pending review row (issue #662, CQR-018). Renders
 * inside CasQuarantineReviewPage's `renderDecisionPanel` seam. Implements
 * the locked state machine from the requirements doc's "Decision-panel
 * state machine" section exactly: Reviewing / ReSearching / CasConflictBlocked,
 * with "reset to original" available at any point in a re-search loop.
 *
 * "Pick one" never re-derives the SCHEMA-1 CAS-conflict policy for the
 * ORIGINAL server-provided candidate set — `candidate.cas_conflict` /
 * `can_confirm` come from CQR-013's detail response as-is. Re-searched
 * candidates (CQR-014) carry no such server-derived flags (that endpoint
 * has no established-CAS context to compute them against), so this panel
 * computes them client-side with the identical formula the server uses —
 * a UX warning only; the confirm route re-checks and 409s regardless, and
 * that 409 renders the same warning (CQR-018.4).
 */

// Mirrors CONFIRMABLE_REASONS (api/sql/casSourceOrchestrator.js) -- reasons
// confirmQuarantinedCandidate() will accept at all. Used only to compute each
// candidate's own annotated `can_confirm` flag; it does NOT gate whether the
// pick list itself renders -- see PICK_LIST_REASONS below.
const CONFIRMABLE_REASONS: CasQuarantineReviewRow['reason'][] = [
  'ambiguous_name',
  'unconfirmed_candidate',
  'blocked_inchikey_mismatch',
];

// Reasons whose decision panel shows a "pick one" list at all -- broader than
// CONFIRMABLE_REASONS. `blocked_cas_disagreement`'s established/offered pair
// must still render (docs/features/cas-quarantine-resolution.md:402,
// BLOCKED-CAS-5 at 2140: "falls to the standard decision panel, established
// and offered both listed by source") even though the offered side can never
// actually be confirmed -- its `cas_conflict` is unconditionally true (that's
// why it was quarantined), so `handlePick`'s own conflict check already
// routes it to the CAS-conflict-blocked warning before any confirm call is
// attempted; picking established still works via reject, same as any other
// reason. `ndc_taint_reverify`/`manufacturer_name_merge` are genuinely
// dismiss-only/split-only (TAINT-3, MERGE-1) and correctly have no pick list
// (Copilot review, PR #792, post-merge round).
const PICK_LIST_REASONS: CasQuarantineReviewRow['reason'][] = [
  ...CONFIRMABLE_REASONS,
  'blocked_cas_disagreement',
];

function computeConflict(
  candidate: { cas_number: string | null },
  establishedCas: string | null
): boolean {
  return Boolean(establishedCas && candidate.cas_number !== establishedCas);
}

function annotateCandidate(
  candidate: CasQuarantineRawCandidate,
  establishedCas: string | null,
  reasonConfirmable: boolean
): CasQuarantineCandidate {
  const casConflict = computeConflict(candidate, establishedCas);
  return {
    ...candidate,
    cas_conflict: casConflict,
    can_confirm: reasonConfirmable && !casConflict,
  };
}

// A dedicated sentinel, never producible by candidateKey() below (real
// candidates key off `source`, never this literal) -- distinguishes the
// synthesized established option from every real candidate unambiguously.
const ESTABLISHED_KEY = '__established__';

// Identifies a candidate by content, not by its position in `displayOptions`.
// The established option is prepended to that array only once `establishedCas`
// is known, which can happen well after this panel first mounts (e.g. a
// sibling row on the same product resolves first and establishes the CAS) --
// a positional index selected before that insertion would otherwise silently
// point at a different candidate afterward (Copilot review, PR #792 round 5).
function candidateKey(candidate: CasQuarantineCandidate): string {
  return `${candidate.source}::${candidate.cas_number ?? candidate.primary_name ?? candidate.names?.[0] ?? ''}`;
}

// PubChem (and possibly other sources) sets `cas_number: null` on a matched
// record that actually exposes several registry numbers, listing them in
// `cas_candidates` instead -- rendering only `cas_number` left that row with
// nothing selectable and Confirm permanently disabled. Split it into one
// pickable option per value in `cas_candidates`, per CQR-018/UNCONF-1's
// requirement to list each candidate CAS value (Copilot review, PR #792
// round 7). Submitting one of these to the confirm route lands on the
// existing live-reverify fallback (it won't match the stored `cas_number:
// null` entry), which independently re-verifies it live -- the same path
// CQR-014's re-searched picks already use.
//
// Each expanded value can individually agree or conflict with the
// established CAS even when the parent (unexpanded) candidate did not --
// `cas_conflict`/`can_confirm` were computed against the parent's single
// (usually null) `cas_number`, so they must be recomputed per expanded value
// rather than inherited (Copilot review, PR #792 round 8).
function expandCandidate(
  candidate: CasQuarantineCandidate,
  establishedCas: string | null,
  reasonConfirmable: boolean
): CasQuarantineCandidate[] {
  if (
    Array.isArray(candidate.cas_candidates) &&
    candidate.cas_candidates.length > 0
  ) {
    return candidate.cas_candidates.map(cas_number => {
      const casConflict = computeConflict({ cas_number }, establishedCas);
      return {
        ...candidate,
        cas_number,
        cas_conflict: casConflict,
        can_confirm: reasonConfirmable && !casConflict,
      };
    });
  }
  return [candidate];
}

// Renders a pick-list option through the same CasSourceCard used by the
// "every source with data on this product" comparison view, instead of a
// single line of label text -- so an admin choosing between e.g. three
// PubChem results sees each one's own matched name, molecular formula,
// SMILES, InChI(Key), and native ID, not just the bare CAS number.
function candidateSourceRow(
  candidate: CasQuarantineCandidate,
  productId: number,
  establishedCas: string | null
): CasQuarantineSourceRow {
  const isEstablishedMatch =
    establishedCas !== null && candidate.cas_number === establishedCas;
  return {
    product_id: productId,
    source: candidate.source,
    match_basis: isEstablishedMatch
      ? 'candidate — matches established'
      : 'candidate',
    cas_number: candidate.cas_number,
    source_native_id: candidate.source_native_id ?? null,
    molecular_formula: candidate.molecular_formula ?? null,
    molecular_weight: candidate.molecular_weight ?? null,
    smiles: candidate.smiles ?? null,
    inchi: candidate.inchi ?? null,
    inchi_key: candidate.inchi_key ?? null,
    raw_response: null,
    queried_at: '',
    matched_name: candidate.primary_name || candidate.names?.[0] || null,
  };
}

// The synthesized established option carries no source-specific detail
// fields of its own -- it's a stand-in for "the product's current value,"
// labeled by whichever real sources already agree with it (see
// buildEstablishedOption below).
function establishedSourceRow(
  option: CasQuarantineCandidate,
  productId: number
): CasQuarantineSourceRow {
  return {
    product_id: productId,
    source: option.source,
    match_basis: 'established',
    cas_number: option.cas_number,
    source_native_id: null,
    molecular_formula: null,
    molecular_weight: null,
    smiles: null,
    inchi: null,
    inchi_key: null,
    raw_response: null,
    queried_at: '',
    matched_name: null,
  };
}

/**
 * Synthesizes the "established" pick option (CQR-018.1: "the list includes
 * every distinct value from every source that has weighed in, established
 * included"). There is no confirm-a-candidate endpoint call for this --
 * picking it submits via reject (nothing changes; the offered batch simply
 * isn't applied), which is exactly what "established stands" means. Labeled
 * by whichever cas_sources rows already agree with it, if any.
 */
function buildEstablishedOption(
  establishedCas: string,
  casSources: CasQuarantineSourceRow[]
): CasQuarantineCandidate {
  const agreeingSources = casSources
    .filter(s => s.cas_number === establishedCas)
    .map(s => s.source);
  return {
    source:
      agreeingSources.length > 0
        ? agreeingSources.join(', ')
        : 'product record',
    cas_number: establishedCas,
    cas_conflict: false,
    can_confirm: true,
  };
}

type Phase = 'reviewing' | 'searching' | 'conflict' | 'alreadyResolved';

// The confirm/reject routes short-circuit to this exact idempotent shape
// (docs/features/cas-quarantine-resolution.md line 463, "Concurrent review":
// the second submit finds the row already resolved and must show the OTHER
// admin's outcome, not silently treat this submit as having succeeded) --
// same message string the split route also uses (Copilot review, PR #792
// round 9).
function isAlreadyResolvedElsewhere(data: { message?: string }): boolean {
  return data.message === 'Review entry already resolved';
}

interface CasDecisionPanelProps {
  productId: number;
  establishedCas: string | null;
  /** Every source with data on this product (CQR-013's cas_sources) -- used
   * only to label the established option by which source(s) agree with it. */
  casSources: CasQuarantineSourceRow[];
  row: CasQuarantineReviewRow;
  onResolved: () => Promise<void>;
}

const CasDecisionPanel: React.FC<CasDecisionPanelProps> = ({
  productId,
  establishedCas,
  casSources,
  row,
  onResolved,
}) => {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);
  const reasonHasConfirmPath = CONFIRMABLE_REASONS.includes(row.reason);
  const reasonShowsPickList = PICK_LIST_REASONS.includes(row.reason);
  const establishedOption = useMemo(
    () =>
      establishedCas !== null
        ? buildEstablishedOption(establishedCas, casSources)
        : null,
    [establishedCas, casSources]
  );

  const [originalCandidates, setOriginalCandidates] = useState<
    CasQuarantineCandidate[]
  >(row.candidates);
  const [candidates, setCandidates] =
    useState<CasQuarantineCandidate[]>(originalCandidates);
  const [isReSearched, setIsReSearched] = useState(false);
  // `quarantine()` can overwrite a pending row's `blocked_payload`
  // concurrently (api/sql/casSourceOrchestrator.js:1797-1815) -- e.g. a
  // sibling row on the same product resolving triggers this page's own
  // refetch, handing this panel a fresh `row` with different candidates.
  // Resync both the saved original and (unless a manual re-search is
  // active) the displayed candidates so an eventual confirm doesn't fail
  // commit-time revalidation against data this panel never showed the
  // admin. Safe to resync `candidates` unconditionally when not
  // re-searched -- `isReSearched` is an independently-tracked boolean, not
  // derived from array identity, so this can't cause the false-positive
  // "re-searched" state a naive reference comparison used to (Copilot
  // review, PR #792, post-merge round).
  useEffect(() => {
    setOriginalCandidates(row.candidates);
    if (!isReSearched) {
      setCandidates(row.candidates);
    }
  }, [row.candidates, isReSearched]);
  const [phase, setPhase] = useState<Phase>('reviewing');
  // Pre-selected on the established option when one exists, per CQR-018.1 --
  // "established is pre-selected/highlighted as the current value," not
  // privileged, just the starting point. Keyed by candidate identity (see
  // candidateKey()), not position, so a later-arriving established option
  // can't silently reinterpret an already-made selection.
  const [selectedKey, setSelectedKey] = useState<string | null>(
    establishedOption ? ESTABLISHED_KEY : null
  );
  // The initializer above only runs at mount. A sibling row on the same
  // product can establish the CAS later, while this row (still pending)
  // stays mounted with nothing selected -- reconcile that case so the
  // established option still ends up preselected, without touching an
  // already-made real selection (round-6 Copilot review, PR #792).
  useEffect(() => {
    if (establishedOption && selectedKey === null) {
      setSelectedKey(ESTABLISHED_KEY);
    }
  }, [establishedOption, selectedKey]);
  const [conflictCandidate, setConflictCandidate] =
    useState<CasQuarantineCandidate | null>(null);

  // The established option (when present) is always index 0; real offered
  // candidates follow. Recomputed each render, not memoized separately from
  // `candidates` -- cheap, and needs to reflect the current working set
  // (original or re-searched) immediately.
  const expandedCandidates = candidates.flatMap(c =>
    expandCandidate(c, establishedCas, reasonHasConfirmPath)
  );
  const displayOptions: CasQuarantineCandidate[] = establishedOption
    ? [establishedOption, ...expandedCandidates]
    : expandedCandidates;
  const isEstablishedKey = (key: string | null): boolean =>
    key === ESTABLISHED_KEY;
  const defaultSelectedKey = establishedOption ? ESTABLISHED_KEY : null;
  const keyOf = (candidate: CasQuarantineCandidate): string =>
    candidate === establishedOption ? ESTABLISHED_KEY : candidateKey(candidate);
  const selectedCandidate = displayOptions.find(c => keyOf(c) === selectedKey);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reSearchError, setReSearchError] = useState<string | null>(null);
  // Distinct from reSearchError (the whole call failed) -- this is a
  // partial-success case: some sources returned candidates, but at least
  // one source errored, so the result set is incomplete even though it
  // isn't empty. Kept separate so the banner wording doesn't claim
  // "re-search failed" when it didn't.
  const [partialSourceErrors, setPartialSourceErrors] = useState<string | null>(
    null
  );
  const [didNothingAck, setDidNothingAck] = useState(false);
  const [anchorInput, setAnchorInput] = useState('');
  const [alreadyResolvedStatus, setAlreadyResolvedStatus] = useState<
    string | null
  >(null);

  const isBusy = isSubmitting || phase === 'searching';

  const isConflictError = (err: unknown): boolean => {
    if (typeof err !== 'object' || err === null || !('response' in err)) {
      return false;
    }
    const response = (err as { response?: { status?: number; data?: unknown } })
      .response;
    if (response?.status !== 409) return false;
    const code =
      typeof response.data === 'object' &&
      response.data !== null &&
      'code' in response.data
        ? (response.data as { code?: unknown }).code
        : undefined;
    return code === 'CAS_RECANONICALIZATION_UNSUPPORTED';
  };

  const handlePick = async () => {
    if (selectedKey === null || !selectedCandidate) return;
    const candidate = selectedCandidate;
    if (candidate.cas_conflict) {
      setConflictCandidate(candidate);
      setPhase('conflict');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = isEstablishedKey(selectedKey)
        ? // Established isn't a real candidate to hand the confirm endpoint --
          // picking it means nothing changes, which is exactly what reject
          // already does (the offered batch simply isn't applied).
          await service.rejectCasCandidate(productId, row.review_id)
        : await service.confirmCasCandidate(
            productId,
            row.review_id,
            candidate
          );
      // Another admin may have already resolved this row (the endpoint's
      // idempotent short-circuit) -- surface THEIR recorded outcome instead
      // of silently treating this submit as having succeeded and advancing
      // past it (docs/features/cas-quarantine-resolution.md line 463;
      // Copilot review, PR #792 round 9).
      if (isAlreadyResolvedElsewhere(response.data)) {
        setAlreadyResolvedStatus(response.data.status ?? null);
        setPhase('alreadyResolved');
        return;
      }
      // Await the parent's refetch/navigation before re-enabling the
      // controls below -- otherwise the stale pending row stays on screen
      // just long enough for a second click to resubmit it (Copilot
      // review, PR #792 round 5).
      await onResolved();
    } catch (err) {
      if (isConflictError(err)) {
        setConflictCandidate(candidate);
        setPhase('conflict');
      } else {
        setSubmitError(
          err instanceof Error ? err.message : 'Failed to confirm candidate'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNoneOfThese = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await service.rejectCasCandidate(
        productId,
        row.review_id
      );
      if (isAlreadyResolvedElsewhere(response.data)) {
        setAlreadyResolvedStatus(response.data.status ?? null);
        setPhase('alreadyResolved');
        return;
      }
      await onResolved();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to reject candidates'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDoNothing = () => {
    setDidNothingAck(true);
  };

  const handleDismissConflict = () => {
    setPhase('reviewing');
    setConflictCandidate(null);
    // Otherwise a stale "left pending" ack from a PRIOR conflict (dismissed,
    // not acted on) survives into the next conflict this row hits, claiming
    // the row was already left pending before the admin has clicked "Do
    // nothing" for this one (Copilot review, PR #792, post-merge round).
    setDidNothingAck(false);
  };

  const handleAcknowledgeAlreadyResolved = async () => {
    setIsSubmitting(true);
    try {
      await onResolved();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setCandidates(originalCandidates);
    setIsReSearched(false);
    setSelectedKey(defaultSelectedKey);
    setReSearchError(null);
    setPartialSourceErrors(null);
    setPhase('reviewing');
  };

  const handleEnterManually = async () => {
    const trimmed = anchorInput.trim();
    if (!trimmed) return;
    setPhase('searching');
    setReSearchError(null);
    setDidNothingAck(false);
    const isCasShaped = /^\d{2,7}-\d{2}-\d$/.test(trimmed);
    try {
      const response = await service.reSearchCasCandidates(
        productId,
        isCasShaped ? { anchorCas: trimmed } : { anchorName: trimmed }
      );
      const sourceErrors = response.data.results.filter(
        r => r.status === 'source_error'
      );
      const flattened = response.data.results.flatMap(r =>
        (r.candidates ?? []).map(c =>
          annotateCandidate(c, establishedCas, reasonHasConfirmPath)
        )
      );
      setReSearchError(null);
      // A partial source failure must stay visible even when OTHER sources
      // returned candidates -- otherwise an incomplete result set (missing
      // whatever that failed source might have offered) presents itself as
      // a complete one, which is exactly the wrong thing for an admin
      // making an identity decision.
      setPartialSourceErrors(
        sourceErrors.length > 0
          ? sourceErrors.map(e => `${e.source}: ${e.error}`).join('; ')
          : null
      );
      if (flattened.length === 0) {
        // Zero candidates is not an error (CQR-018.3) -- return to Reviewing
        // with the ORIGINAL set intact, "none of these" still covers it.
        setCandidates(originalCandidates);
        setIsReSearched(false);
        setSelectedKey(defaultSelectedKey);
      } else {
        setCandidates(flattened);
        setIsReSearched(true);
        setSelectedKey(defaultSelectedKey);
      }
      setPhase('reviewing');
    } catch (err) {
      // Re-search failure -- Reviewing with the original set intact.
      setCandidates(originalCandidates);
      setIsReSearched(false);
      setSelectedKey(defaultSelectedKey);
      setPartialSourceErrors(null);
      setReSearchError(err instanceof Error ? err.message : 'Re-search failed');
      setPhase('reviewing');
    }
  };

  if (phase === 'conflict' && conflictCandidate) {
    return (
      <div style={styles.container}>
        <Alert severity="warning">
          Changing the CAS number here isn&apos;t supported yet — support is
          planned; leave this item open until then.
        </Alert>
        <div style={styles.actionRow}>
          <LgmButton onClick={handleDismissConflict}>
            Dismiss (back to review)
          </LgmButton>
          <LgmButton onClick={handleDoNothing}>Do nothing</LgmButton>
        </div>
        {didNothingAck && (
          <span style={styles.ackText}>Left pending — no changes made.</span>
        )}
      </div>
    );
  }

  if (phase === 'alreadyResolved') {
    return (
      <div style={styles.container}>
        <Alert severity="info">
          {/* Actor-neutral: the same admin retrying after a lost response
              hits this same idempotent path, so "another admin" would be
              false in that case (Copilot review, PR #792, post-merge round). */}
          This item has already been resolved
          {alreadyResolvedStatus
            ? ` — recorded outcome: ${alreadyResolvedStatus}`
            : ''}
          . Your submitted action was not applied.
        </Alert>
        <div style={styles.actionRow}>
          <LgmButton
            onClick={handleAcknowledgeAlreadyResolved}
            disabled={isSubmitting}>
            Continue
          </LgmButton>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {submitError && <Alert severity="error">{submitError}</Alert>}
      {reSearchError && (
        <Alert severity="error">Re-search failed: {reSearchError}</Alert>
      )}
      {partialSourceErrors && (
        <Alert severity="warning">
          Some sources didn&apos;t respond, so this list may be incomplete:{' '}
          {partialSourceErrors}
        </Alert>
      )}

      {reasonShowsPickList && candidates.length > 0 && (
        <RadioGroup
          value={selectedKey ?? ''}
          onChange={e => setSelectedKey(e.target.value)}>
          {(() => {
            // Conflicts are computed across this option list alone, not
            // against the product-wide source comparison -- what matters
            // here is whether the OFFERED options disagree with each
            // other (e.g. three PubChem CAS values), which is exactly
            // what a "pick one" admin needs highlighted.
            const optionSourceRows = displayOptions.map(candidate =>
              candidate === establishedOption
                ? establishedSourceRow(candidate, productId)
                : candidateSourceRow(candidate, productId, establishedCas)
            );
            return displayOptions.map((candidate, index) => {
              const sourceRow = optionSourceRows[index];
              return (
                // Radio + card are siblings, not radio-wraps-card -- CasSourceCard
                // carries its own IconButton, and MUI's FormControlLabel makes its
                // entire `label` one big click target for the radio, which made
                // clicking that button also silently select the option (nested
                // interactive controls, ambiguous keyboard/click/AT behavior).
                // The radio keeps a short, explicit text label of its own for
                // click/AT association; the full card next to it is for reading
                // detail, not for selecting.
                <div
                  key={`${candidate.source}-${index}`}
                  style={styles.optionRow}>
                  <FormControlLabel
                    value={keyOf(candidate)}
                    control={<Radio size="small" disabled={isBusy} />}
                    label={`${sourceRow.source} — ${sourceRow.matched_name ?? sourceRow.cas_number ?? 'unknown value'} (${sourceRow.match_basis})`}
                  />
                  <CasSourceCard
                    source={sourceRow}
                    {...computeFieldConflicts(sourceRow, optionSourceRows)}
                  />
                </div>
              );
            });
          })()}
        </RadioGroup>
      )}

      <div style={styles.actionRow}>
        {reasonShowsPickList && candidates.length > 0 && (
          <LgmButton
            disabled={
              selectedKey === null || isBusy || !selectedCandidate?.cas_number
            }
            onClick={handlePick}>
            {isSubmitting ? 'Confirming…' : 'Confirm selection'}
          </LgmButton>
        )}
        {row.available_actions.can_reject && (
          <LgmButton disabled={isBusy} onClick={handleNoneOfThese}>
            None of these
          </LgmButton>
        )}
        <LgmButton disabled={isBusy} onClick={handleDoNothing}>
          Do nothing
        </LgmButton>
        {isReSearched && (
          <LgmButton disabled={isBusy} onClick={handleReset}>
            Reset to original
          </LgmButton>
        )}
      </div>

      {didNothingAck && (
        <span style={styles.ackText}>Left pending — no changes made.</span>
      )}

      <div style={styles.reSearchRow}>
        <TextField
          size="small"
          placeholder="Enter a name or CAS number"
          value={anchorInput}
          onChange={e => setAnchorInput(e.target.value)}
          disabled={isBusy}
          slotProps={{
            htmlInput: {
              'aria-label': `Enter manually for review ${row.review_id}`,
            },
          }}
        />
        <LgmButton
          disabled={isBusy || !anchorInput.trim()}
          onClick={handleEnterManually}>
          {phase === 'searching' ? (
            <CircularProgress
              size={14}
              thickness={5}
              style={{ color: 'inherit' }}
            />
          ) : (
            'Enter manually'
          )}
        </LgmButton>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  actionRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  reSearchRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  ackText: { color: '#64748b', fontStyle: 'italic', fontSize: '0.85rem' },
  // FormControlLabel centers its label on the radio by default -- wrong
  // once the label is a multi-line card rather than one line of text.
  optionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    marginBottom: '0.5rem',
  },
};

export default CasDecisionPanel;
